import { type PostHog } from 'posthog-js';
import { v4 as uuidv4 } from 'uuid';

import { authService } from '@rikstv/play-common/src/utils/auth/AuthService';
import { debounce } from '@rikstv/play-common/src/utils/eventHandler/debounce';

import SharedConfig from '../../config';

import {
  experimentEntries,
  expStore,
  mapExperimentStoreState,
  PH_EXP_NAMES,
  posthogLoadedStore,
  waitForPosthogFeatureFlags,
} from './posthogExperiments';
import { clearShopperFlow, getShopperFlow, getShopperFlowId, startShopperFlow } from './shopperFlow';

/**
 * HOW TO USE POSTHOG PROVIDER EXPORTS
 *
 * For reporting:
 * Simply import any event or funnel from here and call it (in a useEffect if needed to avoid duplicate events)
 *
 * For experiments:
 *
 * 1. It is useful to use `const { posthogLoaded } = useLoadPosthog()` to ensure that feature flags are loaded before render to avoid flickering
 * 2. Get experiment variant from `const experimentIsOn = usePostHogExperiment('<exp_name>', condition?: boolean)` to get a boolean indicating if the experiment is active (i.e. the user is in the test variation group)
 *
 */

/**
 * Use this hook in situations where you want posthog to init before react renders components that use experiments or events.
 */
export const useLoadPosthog = () => {
  if (!_posthogPromise) {
    lazyLoadClient();
  }
  return posthogLoadedStore();
};

/**
 * Lazy-load posthog client only when events are used to avoid having it part of every bundle.
 * That way it costs us nothing to have it available in the codebase.
 */
let _posthogPromise: Promise<PostHog> | null = null;
const lazyLoadClient = async () => {
  if (_posthogPromise) {
    return _posthogPromise;
  }
  const start = Date.now();
  // Allow max 500ms to wait for posthog loaded state since we might prevent rendering otherwise
  setTimeout(() => posthogLoadedStore.setState({ posthogLoaded: true }), 500);
  _posthogPromise = new Promise(async resolve => {
    const { posthog } = await import('posthog-js');
    posthog.init('phc_kqixdPiJcf3OETOVmnip0rBeBZz6CAZya64q7uN2ZIq', {
      api_host: SharedConfig.application === 'Strim' ? '/ph' : 'https://eu.i.posthog.com',
      ui_host: 'https://eu.posthog.com',
      persistence: 'memory',
      // disable all auto capture
      autocapture: false,
      capture_pageview: false,
      capture_performance: false,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      capture_pageleave: false,
      enable_recording_console_log: false,
      before_send: payload => {
        console.log('[posthog] before_send:', payload);
        return SharedConfig.enablePosthog ? payload : null;
      },
    });
    // Only false in e2e tests where we have mocked feature flag values
    if (waitForPosthogFeatureFlags) {
      posthog.onFeatureFlags(function () {
        posthogLoadedStore.setState({ posthogLoaded: true });
        console.log(`[posthog] initialized in ${Date.now() - start}ms`);
        resolve(posthog);
        expStore.setState(state => {
          // Update store with values based on experiment variation group
          return mapExperimentStoreState((key, name, contender) => {
            // potential override via /dev/null
            if (state[name]) {
              return contender;
            }
            const variant = posthog.getFeatureFlag(key);
            return typeof variant === 'string' ? variant : null;
          });
        });
      });
    } else {
      posthogLoadedStore.setState({ posthogLoaded: true });
      resolve(posthog);
    }
  });
  return _posthogPromise;
};

const reportExperimentImpressionOnce = once(() => {
  const state = expStore.getState();
  // Report experiment impression once per active experiment
  experimentEntries.forEach(([experiment, { key }]) => {
    const variantKey = `${experiment}Variant` as const;
    const group = state[variantKey];
    if (group) {
      window.dataLayer.push({
        event: 'experience_impression',
        experiment_id: key, //unik id for ekseprimentet
        variant_id: group === 'control' ? '0' : '1', //variant, 0=orginal
        experiment_source: 'PH', //LD = Launch Darkly, SAN=Sanity/custom
      });
    }
  });
});

/**
 * Use this hook to get a boolean (true if user is in the contender variant group) for a given experiment. And auto-report impressions to GA.
 * @param experiment - The experiment name
 * @param externalCondition - Optional. When left out or `true` triggers lazy-load of posthog client. When `false` the hook also returns false.
 */
export const usePostHogExperiment = (experiment: PH_EXP_NAMES, externalCondition = true) => {
  if (externalCondition) {
    lazyLoadClient().then(reportExperimentImpressionOnce);
  }

  return expStore()[experiment] && externalCondition;
};

type CaptureProperties = Parameters<PostHog['capture']>[1];

// Read the shopper flow when the event happens, not when it is sent. Debounce and the lazy client load delay
// sending, and by then the flow may have been cleared or replaced by a new one.
const withShopperFlowId = (properties: CaptureProperties) => {
  if (properties && 'shopperFlowId' in properties) {
    return properties;
  }
  return { ...properties, shopperFlowId: getShopperFlowId() ?? undefined };
};

const _captureEvent: PostHog['capture'] = (event_name, properties, options) => {
  const propertiesWithFlow = withShopperFlowId(properties);
  console.log('[posthog] captureEvent:', event_name, propertiesWithFlow, options);
  lazyLoadClient().then(posthog => {
    const userData = authService.getUserData();
    const userId = userData?.userId ?? '';
    const customerId = userData?.cid ?? '';
    posthog.capture(event_name, { ...propertiesWithFlow, userId, customerId }, options);
  });
  return undefined;
};

// Debounce avoids accidental double invocation and rage clicks, because react and hooks...
const _captureEventDebounced = debounce(_captureEvent, 150);
const captureEvent: PostHog['capture'] = (event_name, properties, options) => {
  _captureEventDebounced(event_name, withShopperFlowId(properties), options);
  return undefined;
};

export const salesFunnel = {
  captureViewSubscriptions() {
    captureEvent('strim_funnel_1_select');
  },
  captureViewSelectAddons(products: string[]) {
    captureEvent('strim_funnel_select_addons', { products });
  },
  captureGoToSignUp(products: string[]) {
    captureEvent('strim_funnel_2_signup', { products });
  },
  captureRegisteredUser(products: string[], simplifiedSignUpWithVipps: boolean) {
    captureEvent('strim_funnel_3_registered', { products, simplifiedSignUpWithVipps });
  },
  capturePurchaseSuccess(products: string[], paymentAmount: number) {
    // captureEvent reads the shopper flow synchronously, so the flow can be ended right away
    captureEvent('strim_funnel_4_purchase', { products, paymentAmount, nrOfAddons: products.length - 1 });
    clearShopperFlow();
  },
  captureChangeSuccess(products: string[], purpose: string) {
    captureEvent('strim_funnel_5_change', { products, nrOfAddons: products.length - 1, purpose });
    clearShopperFlow();
  },
};

export const onboardingFunnel = {
  captureOnboardingImpression() {
    captureEvent('strim_ml_quickstart_impression');
  },
  captureOnboardingOpenDialog() {
    captureEvent('strim_ml_quickstart_open_dialog');
  },
  captureOnboardingCompleted(titleCount: number) {
    captureEvent('strim_ml_quickstart_completed', { titleCount });
  },
};

export const singleSignoutFunnel = {
  captureSignoutButtonClick() {
    captureEvent('strim_single_sign_out_button_click');
  },
  captureInfoIconClick() {
    captureEvent('strim_single_sign_out_info_icon_click');
  },
  captureSignoutCompleted() {
    captureEvent('strim_single_sign_out_completed');
  },
};

export const changePasswordFunnel = {
  captureChangePasswordCompleted(singleSignout: boolean) {
    captureEvent('strim_change_password_completed', { singleSignout });
  },
};

export const voucherIdentificationFunnel = {
  captureVoucherIdentificationOpened(voucherCode: string) {
    captureEvent('strim_funnel_step1_voucher_identification_opened', { voucherCode });
  },
  captureVoucherIdentificationSMSSent(voucherCode: string) {
    captureEvent('strim_funnel_step2_voucher_identification_sms_sent', { voucherCode });
  },
  captureVoucherIdentificationCodeValidated(voucherCode: string) {
    captureEvent('strim_funnel_step3_voucher_identification_code_validated', { voucherCode });
  },
};

export const linearCoverpageFunnel = {
  captureClickChannelFromChannelGrid(selectedChannelId: number, selectedChannelIndex: number) {
    captureEvent('linear_coverpage_channel_grid_click', {
      selectedChannelId,
      selectedChannelIndex,
    });
  },

  captureClickFromChannelList(selectedChannelId: number, selectedChannelIndex: number, previousChannelIndex: number) {
    captureEvent('linear_coverpage_channel_list_click', {
      selectedChannelId,
      selectedChannelIndex,
      previousChannelIndex,
    });
  },
};

export const stepIndicator = {
  // create separate debounce here since navigation trigger a new event that cancels the last one if we use the general debounced captureEvent
  captureClickStepIndicator: debounce((label: string, state: string) => {
    _captureEvent('strim_step_indicator_click', { label, state });
  }, 150),
};

export const playerRecoveryFunnel = {
  captureAudioDecodeErrorRecoveryAttempt(videoId: string, currentTime: number) {
    captureEvent('audio_decode_error_recovery_attempt', {
      video_id: videoId,
      current_time: currentTime,
    });
  },
};

const _debouncedByEvent: Record<string, ReturnType<typeof setTimeout>> = {};
const captureEventDebounced: PostHog['capture'] = (event_name, properties, options) => {
  const propertiesWithFlow = withShopperFlowId(properties);
  clearTimeout(_debouncedByEvent[event_name]);
  _debouncedByEvent[event_name] = setTimeout(() => _captureEvent(event_name, propertiesWithFlow, options), 150);
  return undefined;
};

export type ShopperIntent = 'upgrade' | 'signup';

export type ShopperSelectorDetails = {
  isExistingCustomer: boolean;
  subscriptionState?: string;
  newProducts: string[];
  resolvedProducts: string[];
  addedProducts: string[];
  skippedProducts: string[];
  defaultedToBaseProduct?: boolean;
};

export const shopperFunnel = {
  hasActiveFlow() {
    return getShopperFlowId() != null;
  },
  captureStart(assetId: string, intent: ShopperIntent) {
    const { id: shopperFlowId } = startShopperFlow('asset');
    _captureEvent(
      'strim_shopper_1_start',
      { shopperFlowId, assetId, intent, isAuthenticated: authService.isAuthenticated() },
      { send_instantly: true }
    );
  },
  captureRecommendation(
    assetId: string,
    intent: ShopperIntent,
    recommendation: { products: string[]; packageId?: string } | null | undefined
  ) {
    const recommendedProductIds = recommendation?.products ?? [];
    _captureEvent(
      'strim_shopper_2_recommendation',
      {
        assetId,
        intent,
        hasRecommendation: recommendedProductIds.length > 0,
        recommendedProductIds,
        recommendedPackageId: recommendation?.packageId ?? null,
      },
      { send_instantly: true }
    );
  },
  captureSelector(details: ShopperSelectorDetails) {
    // A new-productIds link (campaign, mail, CMS) can start a flow without an asset page click
    const { id: shopperFlowId, source } = getShopperFlow() ?? startShopperFlow('link');
    captureEventDebounced('strim_shopper_3_selector', { source, shopperFlowId, ...details });
  },
};

const SMART_SWITCH_FLOW_ID_KEY = 'strim-smart-switch-flow-id';

const startSmartSwitchFlow = () => {
  const flowId = uuidv4();
  sessionStorage.setItem(SMART_SWITCH_FLOW_ID_KEY, flowId);
  return flowId;
};

export const getSmartSwitchFlowId = () => sessionStorage.getItem(SMART_SWITCH_FLOW_ID_KEY);

const clearSmartSwitchFlow = () => sessionStorage.removeItem(SMART_SWITCH_FLOW_ID_KEY);

export const changeStreamingServiceFunnel = {
  captureInitiateChange(fromVariant: string) {
    const flowId = startSmartSwitchFlow();
    _captureEvent('strim_smart_switch_1_initiate', { fromVariant, flowId }, { send_instantly: true });
  },
  captureSelectVariant(selectedVariant: string, fromVariant: string) {
    captureEventDebounced('strim_smart_switch_2_select_variant', {
      selectedVariant,
      fromVariant,
      flowId: getSmartSwitchFlowId(),
    });
  },
  captureWarningModalShown(targetVariant: string, fromVariant: string, affectedAddons: string[]) {
    captureEventDebounced('strim_smart_switch_3_warning_shown', {
      targetVariant,
      fromVariant,
      affectedAddons,
      flowId: getSmartSwitchFlowId(),
    });
  },
  captureWarningModalConfirmed(targetVariant: string, fromVariant: string, affectedAddons: string[]) {
    captureEventDebounced('strim_smart_switch_3_warning_confirmed', {
      targetVariant,
      fromVariant,
      affectedAddons,
      flowId: getSmartSwitchFlowId(),
    });
  },
  captureWarningModalCancelled(targetVariant: string, fromVariant: string) {
    captureEventDebounced('strim_smart_switch_3_warning_cancelled', {
      targetVariant,
      fromVariant,
      flowId: getSmartSwitchFlowId(),
    });
  },
  abandonFlow() {
    clearSmartSwitchFlow();
  },
  captureReceiptViewed(variant: string, fromVariant: string) {
    captureEventDebounced('strim_smart_switch_4_receipt', { variant, fromVariant, flowId: getSmartSwitchFlowId() });
  },
  captureComplete(action: 'activate' | 'see_content', variant: string, fromVariant: string) {
    _captureEvent(
      'strim_smart_switch_5_complete',
      { action, variant, fromVariant, flowId: getSmartSwitchFlowId() },
      { send_instantly: true }
    );
    clearSmartSwitchFlow();
  },
};

export const prewinbackFunnel = {
  captureEnterCancelSubscription(isInTrialPeriod: boolean) {
    captureEvent('strim_prewinback_enter_cancel_subscription', { isInTrialPeriod });
  },
  capturePrewinbackOfferShown(offers: string[], hasProducts: string[]) {
    captureEvent('strim_prewinback_offer_shown', { offers, hasProducts });
  },
  capturePrewinbackOfferClicked(offer: string, hasProducts: string[]) {
    captureEvent('strim_prewinback_offer_clicked', { offer, hasProducts });
  },
  capturePrewinbackCancellationCompleted() {
    captureEvent('strim_prewinback_cancellation_completed', {});
  },
  capturePrewinbackOfferAccepted(voucherCode: string) {
    captureEvent('strim_prewinback_offer_accepted', { voucherCode });
  },
  // Use event "strim_funnel_4_purchase" to visualize successful purchase after accepting prewinback offer in funnel analysis, and use this event to track successful redemption of prewinback offer
};

/**
 * Call with a function to get a new function that will only call the original function once.
 */
function once(fn: () => void) {
  let called = false;
  return () => {
    if (!called) {
      called = true;
      fn();
    }
  };
}
